'use client';
import { PropsWithChildren, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { SvgClose } from '@/components/icons';
import { IWithClassname } from '@/models/common';
import s from './Modal.module.scss';
import { measureScrollbar } from '@/utils/measureScrollbar';

interface Props extends IWithClassname, PropsWithChildren {
  isOpen: boolean;
  onClose: () => void;
  maxWidth?: string;
}

const Modal = ({ children, isOpen, onClose, maxWidth, className }: Props) => {
  const modalRootRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    modalRootRef.current = document.getElementById('modal-root');
  }, []);

  useEffect(() => {
    const scrollbarWidth = measureScrollbar();

    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.code === 'Escape') onClose();
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      document.addEventListener('keydown', handleKeyPress);
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '0';
    }

    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isOpen]);

  if (!isOpen || !modalRootRef.current) return null;

  return createPortal(
    <div
      className={clsx(s.modal)}
      onClick={onClose}
    >
      <div
        style={{ maxWidth }}
        className={clsx(`${s.modal__container} custom-container`, className)}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={s.modal__close} onClick={onClose}><SvgClose /></div>
        {children}
      </div>
    </div>,
    modalRootRef.current,
  );
};

export default Modal;